import { useEffect, useState } from "react";
import { atom, useRecoilState } from "recoil";
import { getSiteIdForRow } from "../SitesWithLegalEntities";
import DateBox from "devextreme-react/date-box";
import { createSitesClientApi } from "../../../api-client-nswag/taxportal-client-runtime";

export const storeStartValueForCS = atom({
  key: "storeStartValueForCS",
  default: null,
});
export const storeEndValueForCS = atom({
  key: "storeEndValueForCS",
  default: null,
});
export const storeModifiedDatesForCS = atom({
  key: "storeModifiedDatesForCS",
  default: null,
});

interface ModifyDatesProps {
  handleEdit: boolean;
  setBtnContinueDisable?: (disabled: boolean) => void;
}

const dateLabel = { "aria-label": "Date" };

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateOnlyAsLocalDate = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

function ModifyDates({ handleEdit, setBtnContinueDisable }: ModifyDatesProps) {
  const [startValue, setStartValue] = useState<string | null>(null);
  const [endValue, setEndValue] = useState<string | null>(null);

  const [siteIdForUpdate] = useRecoilState<any>(getSiteIdForRow);
  const [modifiedDates, setModifiedDates] = useRecoilState<any>(storeModifiedDatesForCS);

  // Set button disabled on initial render (default true)
  useEffect(() => {
    if (typeof setBtnContinueDisable === "function") {
      setBtnContinueDisable(true);
    }
  }, []);

  useEffect(() => {
    if (siteIdForUpdate != null) {
      createSitesClientApi.taxPortal_ClientMappingDetails(siteIdForUpdate)
        .then((value) => {
          if (value?.siteConfig) {
            JSON.parse(value.siteConfig, (key, val) => {
              if (key === "StartDate" && val) {
                setStartValue(String(val).slice(0, 10));
              } else if (key === "EndDate" && val) {
                setEndValue(String(val).slice(0, 10));
              }
            });
          }
        });
    }
  }, [siteIdForUpdate]);

  useEffect(() => {
    const shouldDisable = !startValue || !endValue || startValue > endValue;
    if (typeof setBtnContinueDisable === "function") {
      setBtnContinueDisable(shouldDisable);
    }

    setModifiedDates(
      JSON.stringify({
        StartDate: startValue,
        EndDate: endValue,
      })
    );
  }, [startValue, endValue, setModifiedDates]);

  const currentYear = new Date().getFullYear();
  const minDate = new Date(currentYear - 10, 0, 1);
  const maxDate = new Date(currentYear + 15, 11, 31);

  const handleStartDateChange = (e: any) => {
    const newValue = e.value;

    if (!newValue) {
      setStartValue(null);
      return;
    }

    const date = newValue instanceof Date ? newValue : new Date(newValue);
    setStartValue(formatLocalDate(date));
  };

  const handleEndDateChange = (e: any) => {
    const newValue = e.value;

    if (!newValue) {
      setEndValue(null);
      return;
    }

    const date = newValue instanceof Date ? newValue : new Date(newValue);
    setEndValue(formatLocalDate(date));
  };

  return (
    <>
      <div>Instruction:</div>
      <div>
        Modify the start and end dates for your client. Refer to site creation form for these details or reach out to Engagement team.
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          margin: "16px 0 0 0",
        }}
      >
        <label style={{ display: "flex", alignItems: "center" }}>Start Date</label>
        <DateBox
          onValueChanged={handleStartDateChange}
          showClearButton
          inputAttr={dateLabel}
          type="date"
          value={startValue ? parseDateOnlyAsLocalDate(startValue) : undefined}
          min={minDate}
          max={maxDate}
          disabled={!handleEdit}
        />
        <label style={{ display: "flex", alignItems: "center" }}>End Date</label>
        <DateBox
          onValueChanged={handleEndDateChange}
          showClearButton
          inputAttr={dateLabel}
          value={endValue ? parseDateOnlyAsLocalDate(endValue) : undefined}
          type="date"
          min={startValue ? parseDateOnlyAsLocalDate(startValue) : minDate}
          max={maxDate}
          disabled={!handleEdit}
        />
      </div>
    </>
  );
}

export default ModifyDates;